import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Switch } from '../components/ui/switch';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import {
  Bell,
  Envelope,
  ChatCircle,
  FunnelSimple,
  MagnifyingGlass,
  Clock,
  FileText,
} from '@phosphor-icons/react';
import EmailTemplatesList from './EmailTemplatesList';
import EmailTemplateEditor from './EmailTemplateEditor';
import { FONCTIONS, STATUT_EMAIL, libelleStatut, familleStatutEmail } from '../lib/libelles';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// RP87-88 — groupes de la liste détaillée (« Avancé ») : les 5 interrupteurs métier,
// puis les e-mails sans interrupteur (backend : services/emails_automatiques.py).
const GROUPES_AVANCE = {
  consultation: 'Consultation des entreprises',
  reunions_cr: 'Réunions et comptes rendus',
  actions: 'Actions et réserves',
  documents_visas: 'Documents et visas',
  paiements: 'Paiements',
  comptes: 'Comptes et accès',
  autres: 'Autres e-mails et SMS',
};

export default function NotificationSettings() {
  const [triggers, setTriggers] = useState([]);
  // RP87 (D37) — 5 interrupteurs métier
  const [categories, setCategories] = useState([]);
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('triggers');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [search, setSearch] = useState('');
  // Templates tab state
  const [selectedTemplateKey, setSelectedTemplateKey] = useState(null);

  useEffect(() => {
    fetchTriggers();
    fetchCategories();
    fetchLogs();
  }, []);

  const fetchCategories = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/emails-automatiques/categories`, { withCredentials: true });
      setCategories(data || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  // RP87 — un interrupteur règle en une fois tous les e-mails de sa catégorie.
  const handleCategorie = async (cat, enabled) => {
    try {
      const { data } = await axios.put(`${API_URL}/api/admin/emails-automatiques/categories/${cat.key}`, { enabled }, { withCredentials: true });
      toast.success(`${cat.label} : ${data.message}`);
      await Promise.all([fetchCategories(), fetchTriggers()]);
    } catch (err) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const fetchTriggers = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/notification-triggers`, { withCredentials: true });
      setTriggers(data);
    } catch (err) {
      toast.error('Erreur de chargement des e-mails automatiques');
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/email-logs?limit=50`, { withCredentials: true });
      setLogs(data.logs);
      setLogsTotal(data.total);
    } catch (err) {
      console.error('Error fetching logs:', err);
    }
  };

  const handleToggle = async (key, currentEnabled) => {
    try {
      await axios.put(`${API_URL}/api/admin/notification-triggers/${key}`, { enabled: !currentEnabled }, { withCredentials: true });
      setTriggers(prev => prev.map(t => t.key === key ? { ...t, enabled: !currentEnabled, reglage_fin: true } : t));
      toast.success(`E-mail automatique ${!currentEnabled ? 'activé' : 'désactivé'}`);
      fetchCategories();
    } catch (err) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const [applyingRecommended, setApplyingRecommended] = useState(false);

  const handleApplyRecommended = async () => {
    if (!window.confirm(
      "Revenir aux réglages par défaut ?\n\n"
      + "Vos réglages actuels (interrupteurs et réglages fins) seront remplacés :\n"
      + "• les e-mails utiles, liés au chantier, seront activés ;\n"
      + "• les e-mails trop fréquents (nouveau message, nouveau document, situations) et les SMS seront désactivés."
    )) return;
    setApplyingRecommended(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/notification-triggers/apply-recommended`,
        {},
        { withCredentials: true },
      );
      toast.success(`Réglages par défaut rétablis : ${data.enabled.length} activés / ${data.disabled.length} désactivés`);
      await Promise.all([fetchTriggers(), fetchCategories()]);
    } catch (err) {
      toast.error("Erreur lors de l'application des réglages par défaut");
    } finally {
      setApplyingRecommended(false);
    }
  };

  const groupeDe = (t) => t.groupe || 'autres';
  const groupes = Object.keys(GROUPES_AVANCE).filter(g => triggers.some(t => groupeDe(t) === g));

  const filteredTriggers = triggers.filter(t => {
    if (categoryFilter !== 'all' && groupeDe(t) !== categoryFilter) return false;
    if (search && !t.label.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const grouped = groupes.reduce((acc, g) => {
    const items = filteredTriggers.filter(t => groupeDe(t) === g);
    if (items.length) acc[g] = items;
    return acc;
  }, {});

  // RP04 — libellé lisible de l'e-mail (jamais la clé technique) pour l'historique.
  const libelleEmail = Object.fromEntries(triggers.map(t => [t.key, t.label]));


  if (loading) {
    return (
      <div className="space-y-4 p-6">
        {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notification-settings">
      {/* Tabs */}
      <div className="flex border-b border-[#E0E0DB]">
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'triggers' ? 'border-[#C75B39] text-[#C75B39]' : 'border-transparent text-[#6B6B6B] hover:text-[#2C2C2C]'}`}
          onClick={() => setActiveTab('triggers')}
          data-testid="tab-triggers"
        >
          <Bell size={16} className="inline mr-2" />
          {FONCTIONS.emailsAutomatiques}
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'logs' ? 'border-[#C75B39] text-[#C75B39]' : 'border-transparent text-[#6B6B6B] hover:text-[#2C2C2C]'}`}
          onClick={() => { setActiveTab('logs'); fetchLogs(); }}
          data-testid="tab-logs"
        >
          <Clock size={16} className="inline mr-2" />
          Historique des envois ({logsTotal})
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'templates' ? 'border-[#C75B39] text-[#C75B39]' : 'border-transparent text-[#6B6B6B] hover:text-[#2C2C2C]'}`}
          onClick={() => { setActiveTab('templates'); setSelectedTemplateKey(null); }}
          data-testid="tab-templates"
        >
          <FileText size={16} className="inline mr-2" />
          Modèles d'e-mails
        </button>
      </div>

      {activeTab === 'templates' && (
        selectedTemplateKey ? (
          <EmailTemplateEditor
            templateKey={selectedTemplateKey}
            onBack={() => setSelectedTemplateKey(null)}
          />
        ) : (
          <EmailTemplatesList
            onSelectTemplate={(key) => setSelectedTemplateKey(key)}
          />
        )
      )}

      {activeTab === 'triggers' && (
        <div className="space-y-4">
          {/* RP87 (D37) — 5 interrupteurs métier */}
          <div className="border border-[#E0E0DB] bg-white divide-y divide-[#E0E0DB]" data-testid="emails-interrupteurs">
            {categories.map(cat => (
              <div key={cat.key} className="flex items-start justify-between gap-4 px-4 py-3" data-testid={`interrupteur-${cat.key}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#2C2C2C]">{cat.label}</p>
                  <p className="text-xs text-[#6B6B6B]">{cat.description}</p>
                  <p className="text-xs text-[#9A9A9A] mt-0.5">
                    {cat.actifs} e-mail{cat.actifs > 1 ? 's' : ''} actif{cat.actifs > 1 ? 's' : ''} sur {cat.total}
                    {cat.personnalise && <span className="ml-2 text-[#B8860B]" data-testid={`interrupteur-personnalise-${cat.key}`}>· réglages fins dans « Avancé »</span>}
                  </p>
                </div>
                <Switch
                  checked={cat.enabled}
                  onCheckedChange={(v) => handleCategorie(cat, v)}
                  aria-label={cat.label}
                  data-testid={`interrupteur-switch-${cat.key}`}
                />
              </div>
            ))}
          </div>
          <p className="text-xs text-[#6B6B6B]">
            {FONCTIONS.reglagesParDefaut} à l'installation. Vos choix sont conservés lors des mises à jour :
            un nouvel e-mail automatique suit l'interrupteur de sa catégorie.
          </p>

          {/* RP88 — la liste détaillée des e-mails automatiques passe dans « Avancé » */}
          <details className="border border-[#E0E0DB] bg-[#FAFAF9]" data-testid="emails-avance">
            <summary className="px-4 py-3 text-sm font-medium text-[#2C2C2C] cursor-pointer">
              Avancé — régler chaque e-mail automatique ({triggers.length})
            </summary>
            <div className="p-4 space-y-4">
          <div className="border border-[#C75B39] bg-[#FFF8F3] p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex-1">
              <p className="text-sm font-bold text-[#2C2C2C] mb-1">Revenir aux réglages par défaut</p>
              <p className="text-xs text-[#6B6B6B]">
                Active les e-mails utiles liés au chantier (comptes rendus, appels d'offres, signatures, AMO…) et coupe les envois trop fréquents (nouveau message, document, situation). Remplace vos réglages actuels.
              </p>
            </div>
            <button
              onClick={handleApplyRecommended}
              disabled={applyingRecommended}
              className="bg-[#C75B39] text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 hover:bg-[#A84A2D] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              data-testid="apply-recommended-config-btn"
            >
              {applyingRecommended ? 'Application…' : 'Rétablir'}
            </button>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[180px] max-w-xs">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9A9A9A]" />
              <input
                type="text"
                placeholder="Rechercher un e-mail automatique..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-[#E0E0DB] text-sm focus:outline-none focus:border-[#2C2C2C]"
                data-testid="trigger-search"
              />
            </div>
            <div className="flex items-center gap-1.5">
              <FunnelSimple size={14} className="text-[#6B6B6B]" />
              <select
                value={categoryFilter}
                onChange={e => setCategoryFilter(e.target.value)}
                className="border border-[#E0E0DB] text-sm px-2 py-2 focus:outline-none"
                data-testid="trigger-category-filter"
              >
                <option value="all">Toutes les catégories</option>
                {groupes.map(g => <option key={g} value={g}>{GROUPES_AVANCE[g]}</option>)}
              </select>
            </div>
          </div>

          {/* Grouped triggers */}
          {Object.entries(grouped).map(([category, items]) => (
            <div key={category} className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold uppercase tracking-wider px-2 py-1 bg-[#F5F5F3] text-[#4A4A4A]">
                  {GROUPES_AVANCE[category] || category}
                </span>
                <span className="text-xs text-[#9A9A9A]">{items.length} e-mail{items.length > 1 ? 's' : ''}</span>
              </div>
              <div className="border border-[#E0E0DB] bg-white divide-y divide-[#E0E0DB]">
                {items.map(trigger => (
                  <div
                    key={trigger.key}
                    className="flex items-center justify-between px-4 py-3 hover:bg-[#FAFAF9] transition-colors"
                    data-testid={`trigger-row-${trigger.key}`}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <div className={`w-8 h-8 flex items-center justify-center ${trigger.type === 'sms' ? 'bg-[#FFEBEE]' : 'bg-[#E3F2FD]'}`}>
                        {trigger.type === 'sms' ? (
                          <ChatCircle size={16} className="text-[#8B3A3A]" />
                        ) : (
                          <Envelope size={16} className="text-[#1E3A5F]" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[#2C2C2C]">{trigger.label}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-[#9A9A9A]">{trigger.type === 'sms' ? 'SMS' : 'E-mail'}</span>
                          {trigger.reglage_fin && <span className="text-xs text-[#B8860B]">· réglage fin</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs font-bold uppercase ${trigger.enabled ? 'text-[#2D6A4F]' : 'text-[#9A9A9A]'}`}>
                        {trigger.enabled ? 'Actif' : 'Inactif'}
                      </span>
                      <Switch
                        checked={trigger.enabled}
                        onCheckedChange={() => handleToggle(trigger.key, trigger.enabled)}
                        data-testid={`trigger-switch-${trigger.key}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
            </div>
          </details>
        </div>
      )}

      {activeTab === 'logs' && (
        <div className="space-y-3">
          {logs.length === 0 ? (
            <div className="text-center py-12 border border-[#E0E0DB] bg-white">
              <Envelope size={32} className="mx-auto text-[#9A9A9A] mb-3" />
              <p className="text-sm text-[#6B6B6B]">Aucun envoi enregistré</p>
            </div>
          ) : (
            <div className="border border-[#E0E0DB] bg-white">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E0E0DB] bg-[#F5F5F3]">
                    <th className="text-left text-xs font-bold uppercase tracking-wider text-[#6B6B6B] px-4 py-2">Type</th>
                    <th className="text-left text-xs font-bold uppercase tracking-wider text-[#6B6B6B] px-4 py-2">E-mail</th>
                    <th className="text-left text-xs font-bold uppercase tracking-wider text-[#6B6B6B] px-4 py-2">Destinataire</th>
                    <th className="text-left text-xs font-bold uppercase tracking-wider text-[#6B6B6B] px-4 py-2">Statut</th>
                    <th className="text-left text-xs font-bold uppercase tracking-wider text-[#6B6B6B] px-4 py-2">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E0E0DB]">
                  {logs.map((log, i) => (
                    <tr key={i} className="hover:bg-[#FAFAF9]">
                      <td className="px-4 py-2">
                        <span className={`text-xs font-bold uppercase px-2 py-0.5 ${log.type === 'sms' ? 'bg-[#FFEBEE] text-[#8B3A3A]' : 'bg-[#E3F2FD] text-[#1E3A5F]'}`}>
                          {log.type}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-sm text-[#4A4A4A]">{libelleEmail[log.template] || log.subject || 'E-mail'}</td>
                      <td className="px-4 py-2 text-sm text-[#2C2C2C]">{log.recipient}</td>
                      <td className="px-4 py-2">
                        <span className={`text-xs font-bold uppercase px-2 py-0.5 ${{ ok: 'bg-[#E8F5E9] text-[#2D6A4F]', echec: 'bg-[#FFEBEE] text-[#8B3A3A]' }[familleStatutEmail(log.status)] || 'bg-[#FFF8E1] text-[#B8860B]'}`}>
                          {libelleStatut(STATUT_EMAIL, log.status)}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs text-[#6B6B6B]">
                        {log.created_at ? new Date(log.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
